import type { App, ComputedRef, InjectionKey } from 'vue'
import { computed, getCurrentInstance, inject, ref } from 'vue'

export type ToastViewportPreset = 'default' | 'right-panel' | 'right-panel-floating'

interface ToastViewportRegistrationOptions {
  priority?: number
}

interface ToastViewportRegistration {
  id: symbol
  preset: ToastViewportPreset
  priority: number
  order: number
}

interface ToastViewportStore {
  currentPreset: ComputedRef<ToastViewportPreset>
  registerToastViewport: (
    preset: ToastViewportPreset,
    options?: ToastViewportRegistrationOptions,
  ) => () => void
}

const TOAST_VIEWPORT_STORE_KEY: InjectionKey<ToastViewportStore> = Symbol('toast-viewport-store')

export const createToastViewportStore = (): ToastViewportStore => {
  const registrations = ref<ToastViewportRegistration[]>([])
  let registrationOrder = 0

  const currentPreset = computed<ToastViewportPreset>(() => {
    const topRegistration = registrations.value.reduce<ToastViewportRegistration | null>(
      (winner, current) => {
        if (!winner) return current
        if (current.priority > winner.priority) return current
        if (current.priority === winner.priority && current.order > winner.order) return current
        return winner
      },
      null,
    )

    return topRegistration?.preset ?? 'default'
  })

  const unregisterToastViewport = (id: symbol) => {
    registrations.value = registrations.value.filter((entry) => entry.id !== id)
  }

  const registerToastViewport = (
    preset: ToastViewportPreset,
    options: ToastViewportRegistrationOptions = {},
  ) => {
    const id = Symbol('toast-viewport')
    let isActive = true

    registrations.value.push({
      id,
      preset,
      priority: options.priority ?? 0,
      order: ++registrationOrder,
    })

    return () => {
      if (!isActive) return
      isActive = false
      unregisterToastViewport(id)
    }
  }

  return {
    currentPreset,
    registerToastViewport,
  }
}

export const createToastViewportStorePlugin = (store = createToastViewportStore()) => ({
  install(app: App) {
    app.provide(TOAST_VIEWPORT_STORE_KEY, store)
  },
})

export const useToastViewportStore = () => {
  const store = inject(TOAST_VIEWPORT_STORE_KEY, null)

  if (store) {
    return store
  }

  const instance = getCurrentInstance()
  if (!instance) {
    throw new Error('Toast viewport store is not available outside of setup')
  }

  const provides = instance.appContext.provides as Record<PropertyKey, unknown>
  const existingStore = provides[TOAST_VIEWPORT_STORE_KEY as symbol]
  if (existingStore) {
    return existingStore as ToastViewportStore
  }

  const localStore = createToastViewportStore()
  provides[TOAST_VIEWPORT_STORE_KEY as symbol] = localStore
  return localStore
}
